import Chunk from "../game/chunk";
import GameMap from "../game/gameMap";
import MapEditorScreen from "../mapEditorScreen";
import { AreaData, AreaNode } from "../types";
import { pointInPolygon } from "../geometry";
import Button from "../ui/button";
import DropDownList from "../ui/dropDownList";
import HorizontalSeparator from "../ui/horizontalSeparator";
import InfiniteSlider from "../ui/infiniteSlider";
import TextInput from "../ui/textInput";
import Label from "../ui/label";
import UIElement from "../ui/uiElement";
import VerticalView from "../ui/verticalView";

// Pixel tolerance (in screen space) for clicking on a node handle or an edge,
// kept constant on screen regardless of zoom level.
const NODE_HIT_RADIUS_PX = 7;
const EDGE_HIT_RADIUS_PX = 6;
const MIN_NODES_WHILE_EDITING = 3;

class AreaTool implements UIElement {
    x: number = 0;
    y: number = 0;
    w: number = 80;
    h: number = 20;
    action: Function;

    selectedArea: AreaData | null = null;
    selectedChunk: Chunk | null = null;

    // ------ Add Area drag state ------
    isAddingArea: boolean = false;
    addAreaStartX: number = 0;
    addAreaStartY: number = 0;
    addAreaCurrentX: number = 0;
    addAreaCurrentY: number = 0;

    // ------ Edit Nodes drag state ------
    draggingNodeIndex: number | null = null;
    hoveredNodeIndex: number | null = null;

    // ------ UI ------
    ui: VerticalView = new VerticalView(0, 0, 10, 10);

    selectToolBtn = new Button(28, 28, "Select Area");
    addAreaBtn = new Button(28, 28, "Add Area");
    editNodesBtn = new Button(28, 28, "Edit Nodes");
    removeAreaBtn = new Button(28, 28, "Remove Area");
    saveChunkBtn = new Button(28, 28, "Save Chunk");

    // ------ Properties ------
    properties: VerticalView = new VerticalView(0, 0, 10, 10);

    drp_areaType = new DropDownList(90, ["wall", "portal", "damage", "trigger"], "Area Type");

    // ------ Area Data Options -------
    defaultAreaView = new VerticalView(0, 0, 90, 100);

    areaDamageView = new VerticalView(0, 0, 90, 100);
    areaDamageSlider = new InfiniteSlider("damage", 90);

    areaPortalView = new VerticalView(0, 0, 90, 100);
    portalTargetX = new InfiniteSlider("pos. X", 90);
    portalTargetY = new InfiniteSlider("pos. Y", 90);
    // Free-text world name so portals can target any world, not a fixed list.
    portalWorldInput = new TextInput(90, 20, "world", "Target world name");
    portalAsk = new DropDownList(90, ["Ask", "Don't ask"], "Ask before teleport");

    areaTriggerView = new VerticalView(0, 0, 90, 100);
    triggerAction = new DropDownList(90, ["action1", "action2", "action3"], "Trigger Action:");
    triggerAsk = new DropDownList(90, ["Ask", "Don't Ask"], "Ask before trigger");

    selectedAreaView: VerticalView = this.defaultAreaView;

    constructor(private gameMap : GameMap, private editor: MapEditorScreen) {
        // ------ UI ------
        this.ui.horizontalAlignment = "LEFT";

        this.selectToolBtn.setImage("/assets/ui/editor/select");
        this.addAreaBtn.setImage("/assets/ui/editor/add");
        this.editNodesBtn.setImage("/assets/ui/editor/move");
        this.removeAreaBtn.setImage("/assets/ui/editor/delete");
        this.saveChunkBtn.setImage("/assets/ui/editor/save");

        this.selectToolBtn.activated = true;

        this.selectToolBtn.action = () => {
            console.log("Select Area");
            this.selectToolBtn.activated = true;
            this.addAreaBtn.activated = false;
            this.editNodesBtn.activated = false;
            this.removeAreaBtn.activated = false;
        }
        this.ui.addElement(this.selectToolBtn);

        this.addAreaBtn.action = () => {
            console.log("Add Area");
            this.selectToolBtn.activated = false;
            this.addAreaBtn.activated = true;
            this.editNodesBtn.activated = false;
            this.removeAreaBtn.activated = false;
        }
        this.ui.addElement(this.addAreaBtn);

        this.editNodesBtn.action = () => {
            console.log("Edit Nodes");
            this.selectToolBtn.activated = false;
            this.addAreaBtn.activated = false;
            this.editNodesBtn.activated = true;
            this.removeAreaBtn.activated = false;
        }
        this.ui.addElement(this.editNodesBtn);

        this.removeAreaBtn.action = () => {
            console.log("Remove Area");
            this.selectToolBtn.activated = false;
            this.addAreaBtn.activated = false;
            this.editNodesBtn.activated = false;
            this.removeAreaBtn.activated = true;
            if(this.selectedArea && this.selectedChunk){
                this.selectedChunk.data.areas = this.selectedChunk.data.areas.filter(a => a !== this.selectedArea);
                this.saveChunk(this.selectedChunk);
                this.selectedArea = null;
                this.selectedChunk = null;
                this.properties.show = false;
                this.selectedAreaView.show = false;
                console.log("Area Removed");
            }
        }
        this.ui.addElement(this.removeAreaBtn);

        this.saveChunkBtn.action = () => {
            if(this.selectedChunk){
                this.saveChunk(this.selectedChunk);
            }
        }
        this.ui.addElement(this.saveChunkBtn);

        // ------ Properties ------

        this.properties.horizontalAlignment = "LEFT";
        this.defaultAreaView.horizontalAlignment = "LEFT";
        this.areaDamageView.horizontalAlignment = "LEFT";
        this.areaPortalView.horizontalAlignment = "LEFT";
        this.areaTriggerView.horizontalAlignment = "LEFT";

        this.properties.show = false;
        this.properties.addElement(new Label("Properties:"));

        let areaTypeLabel = new Label("Area Type:");
        areaTypeLabel.textSize = 10;
        this.properties.addElement(areaTypeLabel);

        this.drp_areaType.action = () => {
            if(this.selectedArea){
                this.selectedArea.type = this.drp_areaType.selectedOption;
                this.updateProperties();
                this.selectedAreaView.show = true;
            }
        }
        this.properties.addElement(this.drp_areaType);

        let nodeHint = new Label("Switch to 'Edit Nodes'\nto reshape this area.");
        nodeHint.textSize = 10;
        this.properties.addElement(nodeHint);

        this.defaultAreaView.addElement(new Label("Area Data:"));
        this.defaultAreaView.addElement(new HorizontalSeparator(90));
        let defaultAreaLabel = new Label("No data available");
        defaultAreaLabel.textSize = 10;
        this.defaultAreaView.addElement(defaultAreaLabel);

        this.selectedAreaView = this.defaultAreaView;
        this.selectedAreaView.show = false;

        // ------ Damage Data ------
        this.areaDamageView.addElement(new Label("Damage Data:"));
        this.areaDamageView.addElement(new HorizontalSeparator(90));
        let damageSlider = new InfiniteSlider("damage", 90);
        damageSlider.action = () => {
            if(this.selectedArea){
                this.selectedArea.damageData = {
                    damage: damageSlider.value
                }
            }
        }
        this.areaDamageView.addElement(damageSlider);

        // ------ Portal Data ------
        this.areaPortalView.addElement(new Label("Portal Data:"));
        this.areaPortalView.addElement(new HorizontalSeparator(90));
        let portalWorldLabel = new Label("Target World:");
        portalWorldLabel.textSize = 10;
        this.areaPortalView.addElement(portalWorldLabel);
        this.portalWorldInput.ALLOWED_CHARS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_";
        this.portalWorldInput.action = () => {
            this.writePortalWorld();
        }
        this.areaPortalView.addElement(this.portalWorldInput);

        this.portalTargetX.action = () => {
            if(this.selectedArea){
                if(!this.selectedArea.portalData){
                    this.selectedArea.portalData = {
                        world: "world1",
                        tx: 0,
                        ty: 0,
                        ask: false
                    }
                }
                this.selectedArea.portalData.tx = this.portalTargetX.value;
            }
        }
        this.areaPortalView.addElement(this.portalTargetX);

        this.portalTargetY.action = () => {
            if(this.selectedArea){
                if(!this.selectedArea.portalData){
                    this.selectedArea.portalData = {
                        world: "world1",
                        tx: 0,
                        ty: 0,
                        ask: false
                    }
                }
                this.selectedArea.portalData.ty = this.portalTargetY.value;
            }
        }
        this.areaPortalView.addElement(this.portalTargetY);

        let portalAskLabel = new Label("Ask before teleport:");
        portalAskLabel.textSize = 10;
        this.areaPortalView.addElement(portalAskLabel);
        this.portalAsk.action = () => {
            if(this.selectedArea){
                if(!this.selectedArea.portalData){
                    this.selectedArea.portalData = {
                        world: "world1",
                        tx: 0,
                        ty: 0,
                        ask: false
                    }
                }
                this.selectedArea.portalData.ask = this.portalAsk.selectedOption === "Ask";
            }
        }
        this.areaPortalView.addElement(this.portalAsk);

        // ------ Trigger Data ------
        this.areaTriggerView.addElement(new Label("Trigger Data:"));
        this.areaTriggerView.addElement(new HorizontalSeparator(90));
        let triggerActionLabel = new Label("Trigger Action:");
        triggerActionLabel.textSize = 10;
        this.areaTriggerView.addElement(triggerActionLabel);
        this.triggerAction.action = () => {
            if(this.selectedArea){
                if(!this.selectedArea.triggerData){
                    this.selectedArea.triggerData = {
                        action: this.triggerAction.selectedOption,
                        ask: false
                    }
                }
                this.selectedArea.triggerData.action = this.triggerAction.selectedOption;
            }
        }
        this.areaTriggerView.addElement(this.triggerAction);

        let triggerAskLabel = new Label("Ask before trigger:");
        triggerAskLabel.textSize = 10;
        this.areaTriggerView.addElement(triggerAskLabel);
        this.triggerAsk.action = () => {
            if(this.selectedArea){
                if(!this.selectedArea.triggerData){
                    this.selectedArea.triggerData = {
                        action: "action1",
                        ask: false
                    }
                }
                this.selectedArea.triggerData.ask = this.triggerAsk.selectedOption === "true";
            }
        }
        this.areaTriggerView.addElement(this.triggerAsk);
    }

    // Ensures the selected area has portalData and copies the world name from
    // the text box into it. Called both on Enter and every frame (see render)
    // so free-typed names are captured even without pressing Enter.
    writePortalWorld(): void {
        if(!this.selectedArea || this.selectedArea.type !== "portal"){
            return;
        }
        if(!this.selectedArea.portalData){
            this.selectedArea.portalData = { world: "", tx: 0, ty: 0, ask: false };
        }
        this.selectedArea.portalData.world = this.portalWorldInput.text;
    }

    updateProperties(): void {
        if(this.selectedArea){
            if(this.selectedArea.type !== "" && this.selectedArea.type){
                this.drp_areaType.selectedOption = this.selectedArea.type;
            }else{
                this.selectedArea.type = "No type";
            }

            if(this.selectedArea.type === "portal"){
                this.selectedAreaView = this.areaPortalView;
                // Reflect the saved world name into the text box on selection.
                this.portalWorldInput.text = this.selectedArea.portalData?.world ?? "";
                console.log("Area properties view changed to Portal");
            }else if(this.selectedArea.type === "damage"){
                this.selectedAreaView = this.areaDamageView;
                console.log("Area properties view changed to Damage");
            }else if(this.selectedArea.type === "trigger"){
                this.selectedAreaView = this.areaTriggerView;
                console.log("Area properties view changed to Trigger");
            }else{
                this.selectedAreaView = this.defaultAreaView;
                console.log("Area properties view changed to Default");
            }
            this.updateLayout();
        }
    }

    updateLayout(): void {
        this.ui.x = p5js.round(-buffer.width/2);
        this.ui.y = p5js.round(-buffer.height/2+this.editor.topBarHeight);
        this.ui.w = 38;
        this.ui.h = buffer.height-this.editor.topBarHeight;
        this.ui.updateLayout();

        this.properties.x = p5js.round(buffer.width/2 - 100);
        this.properties.y = p5js.round(-buffer.height/2+this.editor.topBarHeight);
        this.properties.w = 100;
        this.properties.h = 220;
        this.properties.updateLayout();

        if(this.selectedAreaView){
            this.selectedAreaView.x = p5js.round(buffer.width/2 - 100);
            this.selectedAreaView.y = p5js.round(this.properties.y + this.properties.h);
            this.selectedAreaView.w = 100;
            this.selectedAreaView.h = buffer.height-this.editor.topBarHeight-200;
            this.selectedAreaView.updateLayout();
        }
    }

    // ------ Geometry helpers (chunk-local coordinates) ------

    clampToChunk(x: number, y: number): {x: number, y: number} {
        return {
            x: Math.min(Math.max(x, 0), Chunk.CHUNK_SIZE),
            y: Math.min(Math.max(y, 0), Chunk.CHUNK_SIZE)
        };
    }

    snapIfEnabled(x: number, y: number): {x: number, y: number} {
        if(!this.editor.snapToGrid){
            return {x, y};
        }
        return {x: Math.round(x/16)*16, y: Math.round(y/16)*16};
    }

    // Mouse position in the *selected chunk's* local coordinates (not clamped).
    getMouseLocalToSelectedChunk(): {x: number, y: number} | null {
        if(!this.selectedChunk){
            return null;
        }
        let mPos = this.editor.getMousePosInGame();
        return {
            x: mPos.x - this.selectedChunk.tileX * Chunk.CHUNK_SIZE,
            y: mPos.y - this.selectedChunk.tileY * Chunk.CHUNK_SIZE
        };
    }

    findNodeNear(nodes: AreaNode[], localX: number, localY: number): number {
        let tolerance = NODE_HIT_RADIUS_PX / this.editor.zoomLevel;
        let bestIndex = -1;
        let bestDistSq = tolerance * tolerance;
        for(let i = 0; i < nodes.length; i++){
            let dx = nodes[i].x - localX;
            let dy = nodes[i].y - localY;
            let distSq = dx*dx + dy*dy;
            if(distSq <= bestDistSq){
                bestDistSq = distSq;
                bestIndex = i;
            }
        }
        return bestIndex;
    }

    // Returns the index of the edge (nodes[i] -> nodes[i+1]) closest to the
    // point, if within tolerance, along with the closest point on it. Used to
    // insert a new node when clicking on an edge in Edit Nodes mode.
    findEdgeNear(nodes: AreaNode[], localX: number, localY: number): {index: number, x: number, y: number} | null {
        let tolerance = EDGE_HIT_RADIUS_PX / this.editor.zoomLevel;
        let bestIndex = -1;
        let bestDistSq = tolerance * tolerance;
        let bestX = 0, bestY = 0;

        for(let i = 0; i < nodes.length; i++){
            let a = nodes[i];
            let b = nodes[(i+1) % nodes.length];
            let abx = b.x - a.x;
            let aby = b.y - a.y;
            let lenSq = abx*abx + aby*aby;
            let t = lenSq > 0 ? ((localX - a.x) * abx + (localY - a.y) * aby) / lenSq : 0;
            t = Math.max(0, Math.min(1, t));
            let px = a.x + abx * t;
            let py = a.y + aby * t;
            let dx = localX - px;
            let dy = localY - py;
            let distSq = dx*dx + dy*dy;
            if(distSq <= bestDistSq){
                bestDistSq = distSq;
                bestIndex = i;
                bestX = px;
                bestY = py;
            }
        }

        if(bestIndex === -1){
            return null;
        }
        return {index: bestIndex, x: bestX, y: bestY};
    }

    render() {
        // Keep portalData.world in sync with the text box even if the user
        // never presses Enter (the text input only fires action on Enter).
        this.writePortalWorld();

        // position in game coordinates
        let mPos = this.editor.getMousePosInGame();

        // ------ continuous node dragging (updates every frame, same pattern
        // the tool already used for the in-progress new-area rectangle) ------
        if(this.draggingNodeIndex !== null && this.selectedArea && this.selectedChunk){
            let local = this.getMouseLocalToSelectedChunk();
            if(local){
                let snapped = this.snapIfEnabled(local.x, local.y);
                let clamped = this.clampToChunk(snapped.x, snapped.y);
                this.selectedArea.nodes[this.draggingNodeIndex] = clamped;
            }
        }

        // ------ continuous "add area" rectangle drag ------
        if(this.isAddingArea && this.selectedChunk){
            let local = this.getMouseLocalToSelectedChunk();
            if(local){
                let snapped = this.snapIfEnabled(local.x, local.y);
                let clamped = this.clampToChunk(snapped.x, snapped.y);
                this.addAreaCurrentX = clamped.x;
                this.addAreaCurrentY = clamped.y;
            }

            let minX = Math.min(this.addAreaStartX, this.addAreaCurrentX);
            let minY = Math.min(this.addAreaStartY, this.addAreaCurrentY);
            let maxX = Math.max(this.addAreaStartX, this.addAreaCurrentX);
            let maxY = Math.max(this.addAreaStartY, this.addAreaCurrentY);

            let val = 128+(p5js.sin(p5js.frameCount/10)+1)*64;
            buffer.stroke(255, 0, 255, val);
            buffer.strokeWeight(2);
            buffer.fill(255, 0, 255, val/3);

            let chunkOnScreenX = (this.selectedChunk.tileX * Chunk.CHUNK_SIZE * this.editor.zoomLevel) - this.gameMap.posX * this.editor.zoomLevel;
            let chunkOnScreenY = (this.selectedChunk.tileY * Chunk.CHUNK_SIZE * this.editor.zoomLevel) - this.gameMap.posY * this.editor.zoomLevel;
            buffer.rect(
                chunkOnScreenX + minX * this.editor.zoomLevel,
                chunkOnScreenY + minY * this.editor.zoomLevel,
                (maxX - minX) * this.editor.zoomLevel,
                (maxY - minY) * this.editor.zoomLevel
            );
        }

        // ------ hovered node detection (Edit Nodes mode) ------
        this.hoveredNodeIndex = null;
        if(this.editNodesBtn.activated && this.selectedArea && this.selectedChunk && this.draggingNodeIndex === null){
            let local = this.getMouseLocalToSelectedChunk();
            if(local){
                this.hoveredNodeIndex = this.findNodeNear(this.selectedArea.nodes, local.x, local.y);
                if(this.hoveredNodeIndex === -1){
                    this.hoveredNodeIndex = null;
                }
            }
        }

        //show selected area outline
        if(this.selectedArea && this.selectedChunk && this.selectedArea.nodes.length >= 3){
            let val = 128+(p5js.sin(p5js.frameCount/10)+1)*64;
            buffer.stroke(255, 255, 255, val);
            buffer.strokeWeight(2);
            buffer.fill(255, 255, 255, val/3);

            let chunkOnScreenX = (this.selectedChunk.tileX * Chunk.CHUNK_SIZE * this.editor.zoomLevel) - this.gameMap.posX * this.editor.zoomLevel;
            let chunkOnScreenY = (this.selectedChunk.tileY * Chunk.CHUNK_SIZE * this.editor.zoomLevel) - this.gameMap.posY * this.editor.zoomLevel;

            buffer.beginShape();
            for(let node of this.selectedArea.nodes){
                buffer.vertex(
                    chunkOnScreenX + node.x * this.editor.zoomLevel,
                    chunkOnScreenY + node.y * this.editor.zoomLevel
                );
            }
            buffer.endShape(p5js.CLOSE);

            // node handles, only while Edit Nodes mode is active
            if(this.editNodesBtn.activated){
                for(let i = 0; i < this.selectedArea.nodes.length; i++){
                    let node = this.selectedArea.nodes[i];
                    let sx = chunkOnScreenX + node.x * this.editor.zoomLevel;
                    let sy = chunkOnScreenY + node.y * this.editor.zoomLevel;

                    if(i === this.draggingNodeIndex || i === this.hoveredNodeIndex){
                        buffer.fill(0, 255, 0);
                        buffer.stroke(0);
                    }else{
                        buffer.fill(255);
                        buffer.stroke(0);
                    }
                    buffer.strokeWeight(1);
                    buffer.rectMode(p5js.CENTER);
                    buffer.rect(sx, sy, 6, 6);
                    buffer.rectMode(p5js.CORNER);
                }
            }
        }

        //render new-area-start preview (before dragging begins)
        if(this.addAreaBtn.activated && !this.isAddingArea){
            let screenPos = this.editor.getGamePosOnScreen(mPos.x, mPos.y);
            if(this.editor.snapToGrid){
                screenPos = this.editor.getGamePosOnScreen(Math.round(mPos.x/16) * 16, Math.round(mPos.y/16) * 16);
            }

            buffer.stroke(255, 0, 0);
            buffer.strokeWeight(2);
            buffer.fill(255, 0, 0, 50);
            buffer.rect(screenPos.x, screenPos.y, 16, 16);
        }

        if(this.selectedChunk){
            let cx = this.selectedChunk.tileX * Chunk.CHUNK_SIZE;
            let cy = this.selectedChunk.tileY * Chunk.CHUNK_SIZE;
            let screenPos = this.editor.getGamePosOnScreen(cx, cy);

            buffer.stroke(255, 0, 0);
            buffer.strokeWeight(1);
            buffer.noFill();
            buffer.rect(screenPos.x, screenPos.y, Chunk.CHUNK_SIZE*this.editor.zoomLevel, Chunk.CHUNK_SIZE*this.editor.zoomLevel);
        }

        this.ui.render();
        this.properties.render();
        this.selectedAreaView?.render();
    }

    renderTooltip(): boolean {
        this.ui.renderTooltip();
        this.properties.renderTooltip();
        this.selectedAreaView?.renderTooltip();
        return false;
    }

    saveChunk(chunk: Chunk){
        // Areas with 2 or fewer nodes aren't valid polygons - never persist them,
        // even though normal editing (see keyPressed) shouldn't be able to
        // produce one in the first place.
        let before = chunk.data.areas.length;
        chunk.data.areas = chunk.data.areas.filter(a => a.nodes.length > 2);
        if(chunk.data.areas.length !== before){
            console.warn(`Dropped ${before - chunk.data.areas.length} area(s) with <=2 nodes before saving chunk ${chunk.tileX}_${chunk.tileY}`);
        }

        //chunks persist as part of their region file (see GameMap.saveChunk)
        this.gameMap.saveChunk(chunk);
    }

    findAreaAt(chunk: Chunk, worldX: number, worldY: number): AreaData | null {
        let chunkOffsetX = chunk.tileX * Chunk.CHUNK_SIZE;
        let chunkOffsetY = chunk.tileY * Chunk.CHUNK_SIZE;
        for(let area of chunk.data.areas){
            if(area.nodes.length < 3){
                continue;
            }
            let absNodes = area.nodes.map(n => ({x: chunkOffsetX + n.x, y: chunkOffsetY + n.y}));
            if(pointInPolygon(worldX, worldY, absNodes)){
                return area;
            }
        }
        return null;
    }

    mousePressed(): boolean {
        if(this.ui.mousePressed()){
            return true;
        }
        if(this.properties.mousePressed()){
            return true;
        }
        if(this.selectedAreaView?.mousePressed()){
            return true;
        }

        if(p5js.mouseButton !== p5js.LEFT){
            return false;
        }

        let mPos = this.editor.getMousePosInGame();
        let mx = mPos.x;
        let my = mPos.y;

        // ------ Edit Nodes mode: try to grab/insert a node on the currently
        // selected area first, before falling back to area selection ------
        if(this.editNodesBtn.activated && this.selectedArea && this.selectedChunk){
            let local = this.getMouseLocalToSelectedChunk();
            if(local){
                let nodeIndex = this.findNodeNear(this.selectedArea.nodes, local.x, local.y);
                if(nodeIndex !== -1){
                    this.draggingNodeIndex = nodeIndex;
                    return true;
                }

                let edgeHit = this.findEdgeNear(this.selectedArea.nodes, local.x, local.y);
                if(edgeHit){
                    let snapped = this.snapIfEnabled(edgeHit.x, edgeHit.y);
                    let clamped = this.clampToChunk(snapped.x, snapped.y);
                    this.selectedArea.nodes.splice(edgeHit.index + 1, 0, clamped);
                    this.draggingNodeIndex = edgeHit.index + 1;
                    console.log("Node Added");
                    return true;
                }
            }
            // fall through - clicking empty space lets the user pick a different area
        }

        let chunkX = Math.floor(mx / Chunk.CHUNK_SIZE);
        let chunkY = Math.floor(my / Chunk.CHUNK_SIZE);

        let chunk = this.gameMap.getChunk(chunkX, chunkY);
        if(chunk){
            if(this.selectToolBtn.activated || this.removeAreaBtn.activated || this.editNodesBtn.activated){
                let hit = this.findAreaAt(chunk, mx, my);
                if(hit){
                    if(this.removeAreaBtn.activated){
                        chunk.data.areas = chunk.data.areas.filter(a => a !== hit);
                        this.saveChunk(chunk);
                        this.selectedArea = null;
                        this.selectedChunk = null;
                        this.properties.show = false;
                        this.selectedAreaView.show = false;
                        console.log("Area Removed");
                        return true;
                    }

                    // Select (also used by Edit Nodes mode to switch selection)
                    this.selectedArea = hit;
                    this.selectedChunk = chunk;
                    this.updateProperties();
                    this.properties.show = true;
                    this.selectedAreaView.show = true;
                    console.log("Area Selected");
                    return true;
                }

                if(this.selectToolBtn.activated || this.removeAreaBtn.activated){
                    this.selectedArea = null;
                    this.selectedChunk = null;
                    this.properties.show = false;
                    this.selectedAreaView.show = false;
                }
            }

            if(this.addAreaBtn.activated){
                let localX = mx - chunkX * Chunk.CHUNK_SIZE;
                let localY = my - chunkY * Chunk.CHUNK_SIZE;
                let snapped = this.snapIfEnabled(localX, localY);
                let clamped = this.clampToChunk(snapped.x, snapped.y);

                this.addAreaStartX = clamped.x;
                this.addAreaStartY = clamped.y;
                this.addAreaCurrentX = clamped.x;
                this.addAreaCurrentY = clamped.y;
                this.isAddingArea = true;
                this.selectedChunk = chunk;
                console.log("Area Add Started");
                return true;
            }
        }

        return false;
    }

    mouseReleased(): boolean {
        if(this.draggingNodeIndex !== null){
            this.draggingNodeIndex = null;
            if(this.selectedChunk){
                this.saveChunk(this.selectedChunk);
            }
        }

        if(this.isAddingArea && this.selectedChunk){
            this.isAddingArea = false;

            let minX = Math.min(this.addAreaStartX, this.addAreaCurrentX);
            let minY = Math.min(this.addAreaStartY, this.addAreaCurrentY);
            let maxX = Math.max(this.addAreaStartX, this.addAreaCurrentX);
            let maxY = Math.max(this.addAreaStartY, this.addAreaCurrentY);

            if(maxX - minX > 0 && maxY - minY > 0){
                let newArea: AreaData = {
                    nodes: [
                        {x: minX, y: minY},
                        {x: maxX, y: minY},
                        {x: maxX, y: maxY},
                        {x: minX, y: maxY}
                    ],
                    type: "wall",
                    portalData: null,
                    damageData: null,
                    triggerData: null
                };
                this.selectedChunk.data.areas.push(newArea);
                this.selectedArea = newArea;
                this.properties.show = true;
                this.selectedAreaView.show = true;
                this.updateProperties();
                this.saveChunk(this.selectedChunk);
                console.log("Area Added");
            }
        }

        if(this.ui.mouseReleased()){
            return true;
        }

        if(this.properties.mouseReleased()){
            return true;
        }

        if(this.selectedAreaView?.mouseReleased()){
            return true;
        }

        return false;
    }

    keyPressed(): boolean {

        if(p5js.keyCode === p5js.DELETE){
            if(this.editNodesBtn.activated && this.selectedArea && this.hoveredNodeIndex !== null){
                if(this.selectedArea.nodes.length > MIN_NODES_WHILE_EDITING){
                    this.selectedArea.nodes.splice(this.hoveredNodeIndex, 1);
                    this.hoveredNodeIndex = null;
                    if(this.selectedChunk){
                        this.saveChunk(this.selectedChunk);
                    }
                    console.log("Node Removed");
                }else{
                    console.log(`Cannot remove node: area must have at least ${MIN_NODES_WHILE_EDITING} nodes`);
                }
            }else if(this.selectedArea && this.selectedChunk){
                this.selectedChunk.data.areas = this.selectedChunk.data.areas.filter(a => a !== this.selectedArea);
                this.saveChunk(this.selectedChunk);
                this.selectedArea = null;
                this.selectedChunk = null;
                this.properties.show = false;
                this.selectedAreaView.show = false;
                console.log("Area Removed");
            }
        }

        if(this.ui.keyPressed()){
            return true;
        }

        if(this.properties.keyPressed()){
            return true;
        }

        if(this.selectedAreaView?.keyPressed()){
            return true;
        }

        return false;
    }

    keyReleased(): boolean {
        if(this.ui.keyReleased()){
            return true;
        }

        if(this.properties.keyReleased()){
            return true;
        }

        if(this.selectedAreaView?.keyReleased()){
            return true;
        }

        return false;
    }

    scroll(event: WheelEvent): boolean {
        if(this.ui.scroll(event)){
            return true;
        }

        if(this.properties.scroll(event)){
            return true;
        }

        if(this.selectedAreaView?.scroll(event)){
            return true;
        }

        return false;
    }
}

export default AreaTool;
